import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Warning, FloppyDisk, PlayCircle } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const DEFAULTS = { threshold_pct: -30, day_of_month: 10, enabled: true };

/**
 * FrontPlanDeviationPanel — permite al admin/manager ajustar el umbral %
 * de desviación de obra (default -30) y el día del mes desde el que se
 * evalúa (default 10). Reemplaza los valores hardcoded en operations_router.
 */
export default function FrontPlanDeviationPanel({ canManage }) {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    api.get("/settings/front-plan-deviation")
      .then((r) => setCfg({ ...DEFAULTS, ...(r.data || {}) }))
      .catch(() => setCfg(DEFAULTS))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const t = parseFloat(cfg.threshold_pct);
    const d = parseInt(cfg.day_of_month, 10);
    if (isNaN(t) || t > 0 || t < -100) {
      toast.error("Umbral debe estar entre -100 y 0 (ej: -30 = 30% bajo plan)");
      return;
    }
    if (isNaN(d) || d < 1 || d > 28) {
      toast.error("Día del mes debe estar entre 1 y 28");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/front-plan-deviation", {
        threshold_pct: t, day_of_month: d, enabled: cfg.enabled,
      });
      setCfg({ ...DEFAULTS, ...data });
      toast.success("Umbrales guardados — aplican en el próximo scan");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const runManualScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/operations/front-plans/scan-deviations");
      if (data?.skipped) {
        toast.info(`Scan omitido: ${data.reason || ""}`);
      } else {
        toast.success(`Scan ejecutado — ${data.alerted || 0} nuevas alertas de ${data.checked || 0} planes revisados`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setScanning(false);
    }
  };

  if (loading) return null;

  return (
    <div className="mt-6 border border-amber-200 rounded-lg bg-amber-50/40 p-4" data-testid="front-plan-deviation-panel">
      <div className="flex items-center gap-2 mb-2">
        <Warning size={18} weight="duotone" className="text-amber-700" />
        <h3 className="font-heading font-bold text-slate-800">Alerta Desviación de Obra</h3>
      </div>
      <p className="text-xs text-slate-600 mb-4">
        Notifica al gerente cuando una obra esté por debajo del umbral configurado respecto a su plan mensual,
        a partir del día del mes indicado. Idempotente por (año, mes, obra) — no duplica alertas.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="text-xs font-semibold text-slate-700">Umbral de desviación (%)</Label>
          <Input
            data-testid="fpd-threshold-input"
            type="number"
            step="1" min={-100} max={0}
            disabled={!canManage}
            value={cfg.threshold_pct}
            onChange={(e) => setCfg({ ...cfg, threshold_pct: e.target.value })}
            className="mt-1"
          />
          <p className="text-[10px] text-slate-500 mt-1">
            Valor negativo. Ej: <b>-30</b> = alerta si obra está 30% o más bajo su plan
          </p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Día del mes para evaluar</Label>
          <Input
            data-testid="fpd-day-input"
            type="number"
            step="1" min={1} max={28}
            disabled={!canManage}
            value={cfg.day_of_month}
            onChange={(e) => setCfg({ ...cfg, day_of_month: e.target.value })}
            className="mt-1"
          />
          <p className="text-[10px] text-slate-500 mt-1">
            El scan automático no dispara antes de este día
          </p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Activar alertas</Label>
          <div className="mt-2 flex items-center gap-2 h-9">
            <Switch
              data-testid="fpd-enabled-switch"
              disabled={!canManage}
              checked={!!cfg.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
            />
            <span className="text-sm text-slate-700">{cfg.enabled ? "Habilitado" : "Deshabilitado"}</span>
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {canManage && (
          <Button
            data-testid="fpd-save"
            onClick={save}
            disabled={saving}
            className="gap-1 bg-amber-700 hover:bg-amber-800"
          >
            <FloppyDisk size={14} weight="bold" /> {saving ? "Guardando…" : "Guardar umbrales"}
          </Button>
        )}
        {canManage && (
          <Button
            data-testid="fpd-scan-now"
            variant="outline"
            onClick={runManualScan}
            disabled={scanning}
            className="gap-1"
          >
            <PlayCircle size={14} weight="bold" /> {scanning ? "Escaneando…" : "Ejecutar scan ahora"}
          </Button>
        )}
      </div>
    </div>
  );
}
